import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
    Chip,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Custom Components */
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LanguageTabs from "../../../components/LanguageTabs";
import LectureTimer from "../../../components/LectureTimer";

/* Premium Option‑A Background */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.90), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

// Junior Dev Note: Use semi-transparent dark colors for a premium developer feel
const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    color:"white",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

export default function TypeScriptDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);

    /* TYPESCRIPT LESSONS */
    const lessons = [
        { title: "Intro to TypeScript", link: "/typeScriptIntro" },
        { title: "Basic Types", link: "/typeScriptBasicTypes" },
        { title: "Types vs Interfaces", link: "/typescript-types-vs-interfaces" },
        { title: "Type Aliases & Unions", link: "/typescript-type-aliases-unions" },
        { title: "Enums", link: "/typescript-enums" },
        { title: "Functions", link: "/typescript-functions" },
        { title: "Generics", link: "/typescript-generics" },
        { title: "Type Guards", link: "/typescript-type-guards" },
        { title: "Utility Types", link: "/typescript-utility-types" },
        { title: "Advanced Interfaces", link: "/typescript-advanced-interfaces" },
        { title: "Cheat Sheet", link: "/typescript-cheat-sheet" },
        { title: "Summary & Practice", link: "/typescript-summary" },
        { title: "Quiz Yourself!", link: "/typescript-quiz" },
    ];

    /* Logic: Search & Progress */
    const filteredLessons = useMemo(() => {
        return lessons.filter(lesson =>
            lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const progressPercentage = (completedCount / lessons.length) * 100;

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <PageWrapper>
            {/* ---------------- GOLDEN BANNER ---------------- */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 700,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    TYPESCRIPT FUNDAMENTALS
                </Typography>
            </Box>


            {/* ---------------- TIMER & TABS ---------------- */}
            <LectureTimer />
            <LanguageTabs active="TypeScript" />

            {/* ---------------- MAIN CONTENT ---------------- */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* LEFT COLUMN: Video & Dashboard */}
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}>
                            SELF STUDY DASHBOARD
                        </Typography>

                        <Card sx={{ width: "100%", backgroundColor: "#f5f5f5", textAlign: "center", padding: "15px", borderRadius: "12px", mt: 2 }}>
                            <CardMedia
                                component="iframe"
                                height={isMobile ? "200" : "260"}
                                src="https://www.youtube.com/embed/zQnBQ4tB3ZA"
                                title="Free TypeScript Tutorial"
                                allowFullScreen
                                sx={{ borderRadius: "8px", border: "none", mb: 2 }}
                            />

                            <CardContent sx={{ color: "#100720" }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom>
                                    Frontend Engineering & Beyond
                                </Typography>

                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PersonalVideoIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={600}>
                                        Mode: Self‑paced Modules
                                    </Typography>
                                </Stack>

                                <Link to="/compiler" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            bgcolor: "#100720",
                                            color: "white",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            "&:hover": { bgcolor: "#1a0c36" }
                                        }}
                                    >
                                        Practice Coding
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    </DashboardGlassCard>
                </Grid>

                {/* RIGHT COLUMN: Curriculum with Progress & Search */}
                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 3 }}>
                            TYPESCRIPT CURRICULUM
                        </Typography>

                        {/* Progress Bar Section */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    Mastery Progress
                                </Typography>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    {Math.round(progressPercentage)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { bgcolor: "goldenrod" }
                                }}
                            />
                        </Box>

                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Find a TypeScript topic..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(218, 165, 32, 0.3)" }
                                }
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredLessons.length > 0 ? (
                                filteredLessons.map((lesson, i) => (
                                    <TopicBox key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {lesson.title}
                                            </Typography>
                                        </Box>
                                        <Link to={lesson.link} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">View</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No topics found matching "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </DashboardGlassCard>
                </Grid>
            </Grid>

            {/* Back to Top */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
